import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { gameService, type Game } from '../../../services/gameService';

interface PlayTrackerProps {
  game: Game;
  /** Re-fetch the game row after a tally change so the displayed count stays in sync. */
  onUpdated: () => Promise<void>;
}

/**
 * Replaces the Substitutions tab for a personal (parent/player) account
 * (sc-336 follow-up). Substitutions assume a multi-player roster to sub
 * between, which a one-player personal team never has — this is a simple
 * manual tally instead, for a parent to tap along from the stands.
 */
export const PlayTracker: React.FC<PlayTrackerProps> = ({ game, onUpdated }) => {
  const [saving, setSaving] = useState(false);
  const count = game.play_count ?? 0;

  const adjust = async (delta: number) => {
    const next = Math.max(0, count + delta);
    setSaving(true);
    try {
      await gameService.setPlayCount(game.id, next);
      await onUpdated();
    } catch (error) {
      console.error('[PlayTracker] Error updating play count:', error);
      toast.error('Failed to update play count');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
      <h3 className="text-base font-semibold text-gray-900 mb-1">Play Tracker</h3>
      <p className="text-sm text-gray-500 mb-6">Tap +1 each time your athlete is in on a play.</p>
      <div className="text-5xl font-bold text-primary tabular-nums mb-6">{count}</div>
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => adjust(-1)}
          disabled={saving || count === 0}
          className="w-14 h-14 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Remove one play"
        >
          <Minus className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => adjust(1)}
          disabled={saving}
          className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-dark disabled:opacity-50 shadow-lg transition-colors"
          aria-label="Add one play"
        >
          <Plus className="w-8 h-8" />
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-6">Plays involved in this game</p>
    </div>
  );
};

export default PlayTracker;
